import React, { useEffect } from 'react';
import { Spin, Skeleton } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { LoadingStateHelper } from '../../utils/accessibility';
import { colors, spacing } from './tokens';

interface LoadingStateProps {
  /** What is being loaded (for screen reader announcements) */
  resource?: string;
  /** Loading state variant */
  variant?: 'spinner' | 'skeleton-text' | 'skeleton-card' | 'skeleton-table' | 'inline';
  /** Size of the loading indicator */
  size?: 'small' | 'default' | 'large';
  /** Custom loading text */
  text?: string;
  /** Whether to announce loading to screen readers */
  announceLoading?: boolean;
  /** Custom style */
  style?: React.CSSProperties;
  /** Custom className */
  className?: string;
  /** Number of skeleton rows for skeleton-table variant */
  rows?: number;
  /** Whether to show a minimal loading state */
  minimal?: boolean;
}

const LoadingState: React.FC<LoadingStateProps> = ({
  resource = 'data',
  variant = 'spinner',
  size = 'default',
  text,
  announceLoading = true,
  style,
  className,
  rows = 5,
  minimal = false,
}) => {
  // Announce loading state to screen readers
  useEffect(() => {
    if (announceLoading) {
      LoadingStateHelper.announceLoading(resource);
    }
  }, [resource, announceLoading]);

  const customSpinIcon = <LoadingOutlined style={{ fontSize: size === 'large' ? 32 : size === 'small' ? 16 : 24 }} spin />;

  const baseContainerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: minimal ? spacing.sm : spacing.lg,
    ...style,
  };

  const loadingText = text || `Loading ${resource}...`;

  switch (variant) {
    case 'spinner':
      return (
        <div 
          className={className}
          style={baseContainerStyle}
          role="status" 
          aria-label={loadingText}
          aria-live="polite"
        >
          <Spin 
            indicator={customSpinIcon} 
            size={size}
            tip={!minimal ? loadingText : undefined}
          />
        </div>
      );

    case 'skeleton-text':
      return (
        <div 
          className={className}
          style={{ padding: minimal ? spacing.sm : spacing.lg, ...style }}
          role="status"
          aria-label={loadingText}
          aria-live="polite"
        >
          <Skeleton 
            active 
            paragraph={{ rows: 3, width: ['100%', '90%', '60%'] }}
            title={{ width: '40%' }}
          />
        </div>
      );

    case 'skeleton-card':
      return (
        <div 
          className={className}
          style={{ 
            padding: minimal ? spacing.sm : spacing.lg, 
            backgroundColor: colors.background.secondary,
            borderRadius: '8px',
            border: `1px solid ${colors.background.tertiary}`,
            ...style 
          }}
          role="status"
          aria-label={loadingText}
          aria-live="polite"
        >
          <Skeleton 
            active 
            avatar={{ size: 'large' }}
            paragraph={{ rows: 4 }}
            title
          />
        </div>
      );

    case 'skeleton-table':
      return (
        <div 
          className={className}
          style={{ ...style }}
          role="status"
          aria-label={`Loading ${resource} table...`}
          aria-live="polite"
        >
          <Skeleton 
            active 
            paragraph={{ rows: rows }}
            title={{ width: '30%' }}
          />
          <div style={{ marginTop: spacing.lg }}>
            {Array.from({ length: Math.min(rows, 3) }, (_, index) => (
              <Skeleton 
                key={index}
                active 
                paragraph={{ rows: 1, width: ['60%'] }}
                title={{ width: '20%' }}
                style={{ marginBottom: spacing.sm }}
              />
            ))}
          </div>
        </div>
      );

    case 'inline':
      return (
        <span 
          className={className}
          style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: spacing.xs,
            ...style 
          }}
          role="status"
          aria-label={loadingText}
          aria-live="polite"
        >
          <Spin 
            indicator={customSpinIcon} 
            size="small"
          />
          {!minimal && <span style={{ fontSize: '12px', color: colors.text.secondary }}>{loadingText}</span>}
        </span>
      );

    default:
      return (
        <div 
          className={className}
          style={baseContainerStyle}
          role="status" 
          aria-label={loadingText}
          aria-live="polite"
        >
          <Spin 
            indicator={customSpinIcon} 
            size={size}
            tip={!minimal ? loadingText : undefined}
          />
        </div>
      );
  }
};

/**
 * Higher-order component for adding loading states to any component
 */
interface WithLoadingProps {
  loading: boolean;
  loadingProps?: Omit<LoadingStateProps, 'children'>;
  children: React.ReactNode;
}

export const WithLoading: React.FC<WithLoadingProps> = ({ 
  loading, 
  loadingProps = {}, 
  children 
}) => {
  if (loading) {
    return <LoadingState {...loadingProps} />;
  }
  return <>{children}</>;
};

/**
 * Loading overlay component
 */
interface LoadingOverlayProps extends LoadingStateProps {
  /** Whether the overlay is visible */
  visible: boolean;
  /** Children to overlay */
  children: React.ReactNode;
  /** Overlay opacity */
  opacity?: number;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  visible,
  children,
  opacity = 0.7,
  ...loadingProps
}) => {
  return (
    <div style={{ position: 'relative' }}>
      {children}
      {visible && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: `rgba(255, 255, 255, ${opacity})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: 'inherit',
          }}
          role="status"
          aria-label={`Loading ${loadingProps.resource || 'data'}...`}
          aria-live="polite"
        >
          <LoadingState {...loadingProps} minimal />
        </div>
      )}
    </div>
  );
};

/**
 * Button loading state
 */
interface LoadingButtonProps {
  loading: boolean;
  children: React.ReactNode;
  loadingText?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  [key: string]: any;
}

export const LoadingButton: React.FC<LoadingButtonProps> = ({
  loading,
  children,
  loadingText,
  disabled,
  icon,
  ...props
}) => {
  return (
    <button
      {...props}
      disabled={disabled || loading}
      aria-describedby={loading ? 'loading-description' : undefined}
      style={{
        ...props.style,
        opacity: loading ? 0.7 : 1,
        cursor: loading || disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {loading ? (
        <>
          <Spin size="small" style={{ marginRight: spacing.xs }} />
          {loadingText || children}
          <span id="loading-description" style={{ display: 'none' }}>
            Button is loading, please wait
          </span>
        </>
      ) : (
        <>
          {icon}
          {children}
        </>
      )}
    </button>
  );
};

export default LoadingState;